<template>
  <BaseCard
    v-if="rows.length > 1"
    :heading="$t('register.salonized_pay.dashboard.wallets.report.heading')"
    v-test="'sp-wallet-report'"
  >
    <template #header>
      <BaseSwitch
        v-model="period"
        :options="periodOptions"
        small
        v-test="'sp-wallet-report-period'"
      />
    </template>
    <Flex
      direction="column"
      :gap="1"
    >
      <BaseTable
        :headers="headers"
        :rows="tableRows"
        v-test="'sp-wallet-report-table'"
      />
      <BaseText
        size="s"
        color="secondary"
      >
        {{ $t('register.salonized_pay.dashboard.wallets.report.description', brandNamePay()) }}
      </BaseText>
    </Flex>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Row } from '@/components/base-table/types';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import type { WalletReportRow } from '@/types';
import { GET_ADYEN_WALLET_REPORT } from '../graphql';
import { formatShare, walletName } from './wallet-share';

type Period = 'this_week' | 'last_week' | 'this_month';

const { t } = useI18n();

const period = ref<Period>('this_week');

const periodOptions = computed(() => [
  { label: t('reports.this_week'), value: 'this_week' },
  { label: t('reports.last_week'), value: 'last_week' },
  { label: t('reports.this_month'), value: 'this_month' },
]);

const periodRange = computed(() => {
  const today = dayjs();

  switch (period.value) {
    case 'last_week': {
      const lastWeek = today.subtract(1, 'week');
      return {
        startDate: lastWeek.startOf('isoWeek'),
        endDate: lastWeek.endOf('isoWeek'),
      };
    }
    case 'this_month':
      return {
        startDate: today.startOf('month'),
        endDate: today.endOf('month'),
      };
    default:
      return {
        startDate: today.startOf('isoWeek'),
        endDate: today.endOf('isoWeek'),
      };
  }
});

const { result } = useQuery(GET_ADYEN_WALLET_REPORT, () => ({
  startDate: periodRange.value.startDate.format('YYYY-MM-DD'),
  endDate: periodRange.value.endDate.format('YYYY-MM-DD'),
}));

const rows = computed<WalletReportRow[]>(
  () => result.value?.adyenWalletReport?.rows ?? [],
);

const hasIncome = computed(() => rows.value.some((row) => row.net !== 0));

const headers = computed(() => [
  t('register.salonized_pay.dashboard.wallets.report.wallet'),
  {
    value: t('register.salonized_pay.dashboard.wallets.report.gross'),
    alignRight: true,
  },
  {
    value: t('register.salonized_pay.dashboard.wallets.report.fees'),
    alignRight: true,
  },
  {
    value: t('register.salonized_pay.dashboard.wallets.report.net'),
    alignRight: true,
  },
  {
    value: t('register.salonized_pay.dashboard.wallets.report.share'),
    alignRight: true,
  },
]);

const rowName = (row: WalletReportRow) => {
  if (!row.balanceAccount) {
    return '';
  }

  const name = walletName(row.balanceAccount);
  return row.balanceAccount.isDefault
    ? `${name} · ${t('register.salonized_pay.dashboard.wallets.main_wallet')}`
    : name;
};

const tableRows = computed<Row[]>(() =>
  rows.value.map((row, index) => ({
    id: row.balanceAccount?.id ?? `row-${index}`,
    cells: [
      rowName(row),
      { value: filters.currency(row.gross), alignRight: true },
      {
        value: row.fees ? `− ${filters.currency(row.fees)}` : '',
        alignRight: true,
      },
      { value: filters.currency(row.net), alignRight: true },
      {
        value: hasIncome.value ? formatShare(row.shareBasisPoints) : '',
        alignRight: true,
      },
    ],
  })),
);
</script>
